import React, { useEffect, useState, useCallback } from 'react';
import { FiMail } from 'react-icons/fi';
import { communicationsAPI } from '../../services/api';
import LoadingSpinner from '../../components/common/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

const Messages = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [items, setItems] = useState([]);
  const { user } = useAuth();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      const resp = await communicationsAPI.getMessages();
      const data = resp?.data?.data || resp?.data || {};
      const list = Array.isArray(data.messages) ? data.messages : (Array.isArray(data) ? data : []);
      setItems(list);
    } catch (e) {
      const status = e?.response?.status;
      if (status === 401 && user?.role === 'parent') setError('Please sign in as a parent to view Messages.');
      else if (status === 403 && user?.role === 'parent') setError('Your account is not authorized to view Messages.');
      else setError('Failed to load messages');
    } finally {
      setLoading(false);
    }
  }, [user?.role]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50/60 flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50/60">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        <div className="mb-6">
          <h1 className="text-xl font-semibold text-slate-900 tracking-tight">Messages</h1>
          <p className="text-sm text-slate-500 mt-0.5">Messages from instructors.</p>
        </div>

        {error && (
          <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-200 text-red-800 text-sm">
            {error}
          </div>
        )}

        <div className="rounded-lg border border-slate-200 bg-white shadow-sm overflow-hidden">
          {items.length === 0 ? (
            <div className="text-center py-14 px-6">
              <FiMail className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-sm text-slate-500">No messages yet.</p>
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {items.map((m, i) => (
                <li key={i} className="p-4 flex items-start gap-3 hover:bg-slate-50/50 transition-colors">
                  <div className="h-10 w-10 rounded-full bg-slate-100 flex items-center justify-center text-slate-500 shrink-0">
                    <FiMail className="h-5 w-5" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium text-slate-900">{m.subject || m.title || 'Message'}</div>
                    <div className="text-sm text-slate-600 mt-0.5 line-clamp-2">{m.preview || m.body || ''}</div>
                    <div className="text-xs text-slate-400 mt-1">{m.createdAt ? new Date(m.createdAt).toLocaleString() : ''}</div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </div>
  );
};

export default Messages;
